import { useEffect, useState, useRef } from "react";
import { useAppDispatch } from "@/store";
import { setUser } from "@/user/userSlice";
import { login } from "@/mainApi";

interface LoginModalProps {
  isOpen: boolean;
}

export default function LoginModal({ isOpen }: LoginModalProps) {
  const dispatch = useAppDispatch();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!username.trim() || !password.trim()) {
      setError("Both fields are required.");
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const result = await login(username, password);

      if (!result?.userId) {
        setError("Check the username or password, then please try again.");
      } else {
        result.username = username;
        dispatch(setUser(result));
      }
    } catch (err) {
      setError("Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-[999] flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-white w-full max-w-sm rounded-xl shadow-xl overflow-hidden animate-fadeIn">
        
        <div className="flex items-center gap-2 bg-blue-800 p-3">
          <img src="/logo.svg" alt="Logo" className="w-10 h-10" />
          <h2 className="text-xl font-bold text-black">SM AI: <span className="text-white">Greetings!</span></h2>
        </div>

        <div className="p-5 flex flex-col gap-4">

          <div className="flex flex-col gap-1">
            <label className="font-sm text-slate-700">Hi, my name is Sami!</label>
            <input
              ref={inputRef}
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              className="border border-slate-300 rounded-lg px-3 py-2 bg-slate-50 focus:outline-blue-800"
              placeholder="What's your name?"
              disabled={loading}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="font-sm text-slate-700">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              className="border border-slate-300 rounded-lg px-3 py-2 bg-slate-50 focus:outline-blue-800"
              disabled={loading}
            />
          </div>

          {error && (
            <div className="text-red-600 text-sm">{error}</div>
          )}

          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full bg-blue-800 hover:bg-blue-900 text-white py-2 rounded-lg transition"
          >
            {loading ? "Checking you in…" : "Continue"}
          </button>
        </div>
      </div>
    </div>
  );
}
